import { useNavigate } from 'react-router-dom'
import { usePrivy } from '@privy-io/react-auth'
import { useExportWallet } from '@privy-io/react-auth/solana'
import { useExportWallet as useExportExtendedWallet } from '@privy-io/react-auth/extended-chains'
import { Header, Button, Icon } from '../components/ui'

export function ExportWalletScreen() {
  const navigate = useNavigate()
  const { ready, authenticated, user, exportWallet: exportEthWallet } = usePrivy()
  const { exportWallet: exportSolWallet } = useExportWallet()
  const { exportWallet: exportStellarWallet } = useExportExtendedWallet()

  const isLoading = !ready
  const isAuthenticated = ready && authenticated

  // Check if user has embedded Privy wallets
  const ethWallet = user?.linkedAccounts?.find(
    (account) =>
      account.type === 'wallet' &&
      account.walletClientType === 'privy' &&
      account.chainType === 'ethereum'
  )

  const solWallet = user?.linkedAccounts?.find(
    (account) =>
      account.type === 'wallet' &&
      account.walletClientType === 'privy' &&
      account.chainType === 'solana'
  )

  const stellarWallet = user?.linkedAccounts?.find(
    (account) =>
      account.type === 'wallet' &&
      account.walletClientType === 'privy' &&
      account.chainType === 'stellar'
  )

  const hasAnyWallet = ethWallet || solWallet || stellarWallet

  const handleExportEth = async () => {
    try {
      await exportEthWallet()
    } catch (error) {
      console.error('Failed to export Ethereum wallet:', error)
    }
  }

  const handleExportSol = async () => {
    try {
      await exportSolWallet()
    } catch (error) {
      console.error('Failed to export Solana wallet:', error)
    }
  }

  const handleExportStellar = async () => {
    if (!stellarWallet || stellarWallet.type !== 'wallet') return
    try {
      await exportStellarWallet({ address: stellarWallet.address })
    } catch (error) {
      console.error('Failed to export Stellar wallet:', error)
    }
  }

  return (
    <div className="min-h-[100dvh] w-full bg-[var(--color-background)] flex flex-col">
      <Header title="Export Wallet" showBack onBack={() => navigate(isAuthenticated ? '/capture' : '/')} />

      <main className="flex-1 flex flex-col px-6 py-8">
        {/* Icon */}
        <div className="flex justify-center mb-6">
          <div className="w-20 h-20 rounded-full bg-[var(--color-primary)]/10 flex items-center justify-center">
            <Icon name="lock" size={40} className="text-[var(--color-primary)]" />
          </div>
        </div>

        {/* Title */}
        <h1 className="text-2xl font-semibold text-[var(--color-text)] text-center mb-2">
          Export Private Key
        </h1>

        {/* Description */}
        <p className="text-[var(--color-text-muted)] text-center mb-8">
          Export your wallet's private key to use with other wallet apps like MetaMask.
        </p>

        {/* Warning */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-8">
          <div className="flex gap-3">
            <Icon name="info" size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              <p className="font-medium mb-1">Keep your private key safe</p>
              <p>
                Never share your private key with anyone. Anyone with access to your
                private key can control your wallet and its funds.
              </p>
            </div>
          </div>
        </div>

        {/* Content based on state */}
        <div className="flex-1 flex flex-col justify-end">
          {isLoading && (
            <div className="text-center py-8">
              <div className="w-8 h-8 border-2 border-[var(--color-primary)] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
              <p className="text-[var(--color-text-muted)] text-sm">Loading...</p>
            </div>
          )}

          {!isLoading && !isAuthenticated && (
            <div className="text-center py-8">
              <p className="text-[var(--color-text-muted)] mb-4">
                You need to be logged in to export your wallet.
              </p>
              <Button variant="outline" onClick={() => navigate('/')}>
                Go to Login
              </Button>
            </div>
          )}

          {!isLoading && isAuthenticated && !hasAnyWallet && (
            <div className="text-center py-8">
              <p className="text-[var(--color-text-muted)]">
                No embedded wallet found for your account.
              </p>
            </div>
          )}

          {!isLoading && isAuthenticated && hasAnyWallet && (
            <div className="space-y-3">
              {ethWallet && (
                <Button onClick={handleExportEth}>
                  <Icon name="lock" size={20} />
                  Export Ethereum Wallet
                </Button>
              )}
              {solWallet && (
                <Button variant={ethWallet ? 'outline' : 'primary'} onClick={handleExportSol}>
                  <Icon name="lock" size={20} />
                  Export Solana Wallet
                </Button>
              )}
              {stellarWallet && (
                <Button variant={ethWallet || solWallet ? 'outline' : 'primary'} onClick={handleExportStellar}>
                  <Icon name="lock" size={20} />
                  Export Stellar Wallet
                </Button>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  )
}
